/**
 * Thin GA4 helpers. Safe no-ops when gtag is unavailable or analytics consent
 * is not accepted. Never send PII (names, emails, org names, free-text messages).
 */

import { hasAnalyticsConsent } from './consent';

const MEASUREMENT_ID = 'G-6KZEZ8XP91';

function gtag() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag.apply(null, arguments);
}

/**
 * @param {string} eventName
 * @param {Record<string, string|number|boolean|undefined>} [params]
 */
export function trackEvent(eventName, params) {
  if (!eventName || !hasAnalyticsConsent()) {
    return;
  }
  gtag('event', eventName, params || {});
}

/**
 * SPA route change page views (gtag only auto-fires the first document load).
 * @param {string} [path]
 * @param {string} [title]
 */
export function trackPageView(path, title) {
  if (!hasAnalyticsConsent()) {
    return;
  }
  const pagePath =
    path ||
    (typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash || ''}`
      : '/');
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: title || (typeof document !== 'undefined' ? document.title : undefined),
    send_to: MEASUREMENT_ID
  });
}

/**
 * Grant Consent Mode analytics storage and send a page_view for the current route.
 * Call after the user Accepts cookies mid-session.
 */
export function enableAnalyticsConsent() {
  gtag('consent', 'update', {
    analytics_storage: 'granted'
  });
  trackPageView();
}

/**
 * Keep analytics denied (Reject / essential only).
 */
export function disableAnalyticsConsent() {
  gtag('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
}

/**
 * Primary CTA clicks that scroll/navigate toward the contact form.
 * @param {string} location - e.g. hero, header, strategy_review, services_hub, service_detail
 */
export function trackCtaClick(location) {
  trackEvent('cta_click', {
    cta_id: 'schedule_strategy_review',
    cta_location: location || 'unknown'
  });
}

/**
 * Successful contact form submission (conversion candidate in GA4).
 * @param {string} [interest]
 * @param {string} [method] - form | webmcp
 */
export function trackGenerateLead(interest, method) {
  trackEvent('generate_lead', {
    lead_type: 'technology_strategy_review',
    interest: interest || 'unspecified',
    method: method || 'form'
  });
}
